/**
 * The decisions a merge asks staff to make, kept apart from the screen so they can be tested on their own. Everything
 * else about a merge follows fixed rules in the database (ADR 0030).
 */

/** The details a merge lets staff choose between. */
export interface MergeSide {
  readonly id: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly preferredName: string | null;
  /** ISO date, or null. */
  readonly birthdate: string | null;
  readonly createdAt: string;
}

export type ChoiceField = "firstName" | "lastName" | "preferredName" | "birthdate";

export const CHOICE_FIELDS: readonly { readonly key: ChoiceField; readonly label: string }[] = [
  { key: "firstName", label: "First name" },
  { key: "lastName", label: "Last name" },
  { key: "preferredName", label: "Goes by" },
  { key: "birthdate", label: "Birthdate" },
];

/** For each detail, which record's value the kept record ends up with. */
export type Choices = Record<ChoiceField, "keep" | "merge">;

/** What staff see for why two people were paired, strongest reason first. */
export const REASON_LABELS: Readonly<Record<string, string>> = {
  email: "Same email",
  phone: "Same phone",
  name_birthdate: "Same name and birthdate",
  name: "Same name",
};

const REASON_ORDER = ["email", "phone", "name_birthdate", "name"];

export function describeReasons(reasons: readonly string[]): string[] {
  return [...reasons]
    .sort((a, b) => REASON_ORDER.indexOf(a) - REASON_ORDER.indexOf(b))
    .map((reason) => REASON_LABELS[reason] ?? reason);
}

/** The record kept by default: the one the church entered first, which other records are most likely to refer to. */
export function defaultKeep(a: MergeSide, b: MergeSide): { keep: MergeSide; merge: MergeSide } {
  return a.createdAt <= b.createdAt ? { keep: a, merge: b } : { keep: b, merge: a };
}

/** Whether the two records disagree on a detail, so there is a choice to offer. */
export function differs(keep: MergeSide, merge: MergeSide, field: ChoiceField): boolean {
  return (keep[field] ?? "") !== (merge[field] ?? "");
}

/** Keep the kept record's details, except where it has nothing and the other record has something. */
export function defaultChoices(keep: MergeSide, merge: MergeSide): Choices {
  const pick = (field: ChoiceField) =>
    (keep[field] ?? "") === "" && (merge[field] ?? "") !== "" ? "merge" : "keep";
  return {
    firstName: pick("firstName"),
    lastName: pick("lastName"),
    preferredName: pick("preferredName"),
    birthdate: pick("birthdate"),
  };
}

export interface MergeInput {
  readonly keepId: string;
  readonly mergeId: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly preferredName: string | null;
  readonly birthdate: string | null;
}

export function mergeInput(keep: MergeSide, merge: MergeSide, choices: Choices): MergeInput {
  const from = (field: ChoiceField) => (choices[field] === "keep" ? keep : merge)[field];
  return {
    keepId: keep.id,
    mergeId: merge.id,
    firstName: from("firstName") ?? keep.firstName,
    lastName: from("lastName") ?? keep.lastName,
    preferredName: from("preferredName"),
    birthdate: from("birthdate"),
  };
}

/** What the merge function reports it moved, as the sentence staff read afterwards. */
export function describeMoved(moved: Readonly<Record<string, number>>): string {
  const parts: [string, string, string][] = [
    ["contacts", "contact", "contacts"],
    ["tags", "tag", "tags"],
    ["notes", "note", "notes"],
    ["activities", "history entry", "history entries"],
    ["custom_fields", "field", "fields"],
    ["preferences", "communication preference", "communication preferences"],
    ["households", "household", "households"],
  ];
  const said = parts
    .filter(([key]) => (moved[key] ?? 0) > 0)
    .map(([key, one, many]) => {
      const n = moved[key] ?? 0;
      return `${String(n)} ${n === 1 ? one : many}`;
    });
  return said.length === 0 ? "Nothing else needed moving." : `Moved ${said.join(", ")}.`;
}
